import { useParams } from "react-router-dom";
import logoMark from "@/assets/bdse-logo-mark.png";
import { shortDate } from "@/features/signals/format";
import {
  useRecipientActions,
  useRecipientBrief,
  useResolvedIdentity,
} from "@/features/recipient/hooks";
import type { RecipientSignal } from "@/features/recipient/types";
import { RecipientTopBar } from "@/features/recipient/components/RecipientTopBar";
import { RecipientMain } from "@/features/recipient/components/RecipientMain";
import { RecipientRail } from "@/features/recipient/components/RecipientRail";

/**
 * Full-page Magic Link recipient view at /s/:token — Reskin Session C.
 *
 * Rebuilt onto the sealed signal-detail anatomy as a STANDALONE page: the whole
 * surface renders inside a `.sx-modal` container (no scrim, no Escape-to-close,
 * the header scrolls with the page) so every sealed zone / button / field rule
 * from signal-detail-redesign.css applies unchanged. The sx- tokens are carried
 * by the `.sxr-app` wrapper since this public route renders with no AppShell.
 *
 * Recipient-tier gates (ruling 5) are preserved: no claim/unclaim, assign, or
 * archive; ownership renders as read-only state. A dead token still resolves to
 * a null brief and renders the honest expired fallback, never signal content.
 *
 * Hydration is skeleton-first (ruling: no all-or-nothing loader): the page
 * chrome paints immediately and each zone shows skeleton lines until the single
 * brief fetch lands, then swaps to content. Per-section absence (no why, no
 * pattern, no asset, empty contacts) degrades honestly inside each component.
 */
export function RecipientSignalPage(): JSX.Element {
  const { token } = useParams<{ token?: string }>();
  const query = useRecipientBrief(token ?? null);
  const actions = useRecipientActions(token ?? "");
  const brief = query.data ?? null;
  // Resolve the assigner identity only once the brief has failed (dead token).
  const identity = useResolvedIdentity(
    token ?? null,
    !query.isPending && brief === null,
  );

  return (
    <div className="sxr-page">
      <div className="sxr-app">
        {query.isPending ? (
          <RecipientSkeleton />
        ) : brief ? (
          <div className="sx-modal sxr-modal">
            <RecipientTopBar brief={brief} actions={actions} />
            <RecipientHeader signal={brief.signal} ownershipLabel={brief.ownershipLabel} />
            <div className="sx-grid">
              <div className="sx-col">
                <RecipientMain brief={brief} actions={actions} />
              </div>
              <div className="sx-col">
                <RecipientRail brief={brief} actions={actions} />
              </div>
            </div>
          </div>
        ) : query.isError ? (
          // A load failure is NOT a dead token — say so honestly and offer retry,
          // rather than claiming the link expired. (A genuinely dead token
          // resolves to a null brief with no error and falls through below.)
          <LoadError onRetry={() => query.refetch()} />
        ) : (
          <ExpiredFallback assignerName={identity.data?.assignerName ?? null} />
        )}
      </div>
    </div>
  );
}

const VERIF_LABEL: Record<RecipientSignal["verification"], string> = {
  verified: "Verified",
  unverified: "Unverified",
  flagged: "Flagged",
};

/**
 * Zone 1 header (sealed anatomy), recipient variant: score + tier, the mono
 * company/verification eyebrow, the headline, and the read-only ownership line.
 * No star and no action row (recipient-tier gate). The score breakdown tooltip
 * is omitted honestly — the recipient brief carries no score breakdown.
 */
function RecipientHeader({
  signal,
  ownershipLabel,
}: {
  signal: RecipientSignal;
  ownershipLabel: string;
}): JSX.Element {
  const updated = signal.updatedAt
    ? ` · Updated ${shortDate(signal.updatedAt)}`
    : "";
  const firstSeen = signal.firstSeenAt
    ? ` · First seen ${shortDate(signal.firstSeenAt)}`
    : "";
  return (
    <header className="sx-z-header">
      <div className="sx-hd-top">
        <div className="sx-score">
          <span className="sx-val">
            {signal.score}
            <span className="sx-den">/100</span>
          </span>
          <span className="sx-tier" style={{ display: "block" }}>
            TIER {signal.tier}
          </span>
        </div>
        <div className="sx-hd-main">
          <p className="sx-meta">
            {signal.company} · {VERIF_LABEL[signal.verification]}
          </p>
          <h1 className="sx-headline" id="sig-headline">
            {signal.headline}
          </h1>
          <p className="sx-meta" style={{ color: "var(--action-text)" }}>
            <span className="sx-dot" />
            {`${ownershipLabel}${updated}${firstSeen}`}
          </p>
        </div>
      </div>
    </header>
  );
}

/**
 * Skeleton hydration shell: the page chrome + zone scaffolding paint instantly;
 * skeleton lines stand in for the brief until the single fetch lands.
 */
function RecipientSkeleton(): JSX.Element {
  return (
    <div className="sx-modal sxr-modal" aria-busy="true">
      <div className="sxr-topbar">
        <img className="sxr-logo" src={logoMark} alt="BD Signal Engine" />
        <span className="sx-skel" style={{ width: 220, height: 12 }} />
      </div>
      <header className="sx-z-header">
        <div className="sx-hd-top">
          <div className="sx-score">
            <span className="sx-skel" style={{ width: 52, height: 26 }} />
          </div>
          <div className="sx-hd-main" style={{ flex: 1 }}>
            <span className="sx-skel" style={{ width: "38%", height: 11, marginBottom: 9 }} />
            <span className="sx-skel" style={{ width: "72%", height: 20, marginBottom: 9 }} />
            <span className="sx-skel" style={{ width: "48%", height: 11 }} />
          </div>
        </div>
      </header>
      <div className="sx-grid">
        <div className="sx-col">
          <div className="sx-z-intel">
            <span className="sx-skel" style={{ width: "30%", height: 11, marginBottom: 9 }} />
            <span className="sx-skel" style={{ width: "100%", height: 12, marginBottom: 7 }} />
            <span className="sx-skel" style={{ width: "92%", height: 12, marginBottom: 7 }} />
            <span className="sx-skel" style={{ width: "68%", height: 12 }} />
          </div>
        </div>
        <div className="sx-col">
          <div className="sx-z-activation">
            <span className="sx-skel" style={{ width: "50%", height: 11, marginBottom: 9 }} />
            <span className="sx-skel" style={{ width: "100%", height: 12, marginBottom: 7 }} />
            <span className="sx-skel" style={{ width: "80%", height: 12 }} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Transient load-failure state — distinct from an expired/dead token. Shown when
 * the brief fetch errors (network/server), so the page never mislabels a load
 * failure as an expired link.
 */
function LoadError({ onRetry }: { onRetry: () => void }): JSX.Element {
  return (
    <div className="sxr-fallback">
      <img className="sxr-logo" src={logoMark} alt="BD Signal Engine" />
      <p className="sxr-fallback-title">This signal didn't load.</p>
      <p className="sxr-fallback-body">
        Something interrupted the connection.{" "}
        <button type="button" className="sx-btn sx-btn-p3" onClick={onRetry}>
          Try again
        </button>
      </p>
    </div>
  );
}

/**
 * Expired / invalid / revoked token fallback (spec §2, §7). Never renders signal
 * content on a dead token. Names the assigner when identity is still resolvable
 * and shows the sign-in nudge once it resolves.
 */
function ExpiredFallback({
  assignerName,
}: {
  assignerName: string | null;
}): JSX.Element {
  return (
    <div className="sxr-fallback">
      <img className="sxr-logo" src={logoMark} alt="BD Signal Engine" />
      <p className="sxr-fallback-title">This link has expired.</p>
      <p className="sxr-fallback-body">
        Ask {assignerName ?? "the person who shared this link"} to share a new
        one.
        {assignerName ? (
          <>
            {" "}
            Or <a href="/login">sign in to BDSE</a> to see your assigned signals.
          </>
        ) : null}
      </p>
    </div>
  );
}
